import React, { useState } from 'react';
import { FiCheckCircle, FiClock, FiFileText, FiLock, FiPlus, FiTrash2 } from 'react-icons/fi';
import type { HREmployee, HrEvent } from './hrData';
import { localDateKey, type CreateHREventPayload } from '../../services/hrService';
import { usDate } from './licenseUtils';

interface Props {
  admin: boolean;
  /** Admin: everyone, for the "who is this about" picker. */
  employees: HREmployee[];
  /** Admin: every event. Everyone else: their own, minus admin-only ones (the API filters). */
  events: HrEvent[];
  busy: boolean;
  /** Resolves true once saved; false keeps the form filled in (the page shows the error). */
  onCreate: (payload: CreateHREventPayload) => Promise<boolean>;
  onDelete: (id: number) => void;
  onAcknowledge: (id: number) => void;
}

const TYPE_SUGGESTIONS = ['Note', 'Performance Review', 'Recognition', 'Training', 'Warning'];

const dateLabel = (iso: string) => new Date(iso).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });

/** Recent HR events — admins add and remove them; employees see their own and acknowledge when asked. */
const HREvents: React.FC<Props> = ({ admin, employees, events, busy, onCreate, onDelete, onAcknowledge }) => {
  const [adding, setAdding] = useState(false);
  const [employeeId, setEmployeeId] = useState('');
  const [typeLabel, setTypeLabel] = useState('Note');
  const [eventDate, setEventDate] = useState(localDateKey());
  const [body, setBody] = useState('');
  const [requestAck, setRequestAck] = useState(false);
  const [adminOnly, setAdminOnly] = useState(false);

  if (!admin && events.length === 0) return null;

  const canSave = !!employeeId && !!typeLabel.trim() && !!eventDate && !!body.trim() && !busy;

  const save = async () => {
    if (!canSave) return;
    const saved = await onCreate({
      employeeId: Number(employeeId),
      typeLabel: typeLabel.trim(),
      eventDate,
      body: body.trim(),
      requestAcknowledgement: requestAck && !adminOnly,
      adminOnly,
    });
    if (saved) {
      setAdding(false);
      setEmployeeId(''); setTypeLabel('Note'); setEventDate(localDateKey()); setBody('');
      setRequestAck(false); setAdminOnly(false);
    }
  };

  return (
    <section className="hr__events">
      <div className="hr__events-head">
        <h3 className="hr__events-title">{admin ? 'Recent HR events' : 'Your HR events'}</h3>
        {admin && !adding && (
          <button type="button" className="hr-dir__allocate-btn" onClick={() => setAdding(true)}>
            <FiPlus size={12} aria-hidden="true" /> Add HR event
          </button>
        )}
      </div>

      {admin && adding && (
        <div className="hr-dir__edit hr__events-form">
          <label className="hr-to__field">
            Employee
            <select value={employeeId} onChange={e => setEmployeeId(e.target.value)}>
              <option value="">Choose…</option>
              {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </label>
          <label className="hr-to__field">
            Type
            <input list="hr-event-types" value={typeLabel} maxLength={100} onChange={e => setTypeLabel(e.target.value)} />
            <datalist id="hr-event-types">
              {TYPE_SUGGESTIONS.map(t => <option key={t} value={t} />)}
            </datalist>
          </label>
          <label className="hr-to__field">
            Date
            <input type="date" value={eventDate} onChange={e => setEventDate(e.target.value)} />
          </label>
          <label className="hr-to__field hr__events-body-field">
            Details
            <textarea rows={2} maxLength={2000} value={body} onChange={e => setBody(e.target.value)} />
          </label>
          <label className="hr-settings__check">
            <input type="checkbox" checked={adminOnly} onChange={e => setAdminOnly(e.target.checked)} />
            Admin only (hidden from the employee)
          </label>
          <label className="hr-settings__check">
            <input type="checkbox" checked={requestAck && !adminOnly} disabled={adminOnly} onChange={e => setRequestAck(e.target.checked)} />
            Ask them to acknowledge
          </label>
          <span className="hr-dir__edit-actions">
            <button type="button" className="hr-dir__allocate-save" disabled={!canSave} onClick={save}>Save</button>
            <button type="button" className="hr-dir__allocate-cancel" onClick={() => setAdding(false)}>Cancel</button>
          </span>
        </div>
      )}

      {events.length === 0 ? (
        <p className="hr-dash__empty">No HR events yet.</p>
      ) : (
        <ul className="hr__events-list">
          {events.map(e => {
            const awaiting = e.requestAcknowledgement && !e.acknowledgedAt;
            return (
              <li key={e.id} className="hr__event">
                <span className="hr__event-icon">
                  {e.adminOnly ? <FiLock size={12} /> : e.acknowledgedAt ? <FiCheckCircle size={12} /> : awaiting ? <FiClock size={12} /> : <FiFileText size={12} />}
                </span>
                <span className="hr__event-main">
                  <strong>{e.typeLabel}</strong>{admin && <> — {e.employeeName}</>} · {usDate(e.eventDate)}
                  <span className="hr__event-body">{e.body}</span>
                  {e.adminOnly && <span className="hr__event-meta">Admin only</span>}
                  {e.acknowledgedAt && <span className="hr__event-meta">Acknowledged {dateLabel(e.acknowledgedAt)}</span>}
                  {awaiting && admin && <span className="hr__event-meta hr__event-meta--warn">Awaiting acknowledgement</span>}
                </span>
                {awaiting && !admin && (
                  <button type="button" className="hr-to__approve" disabled={busy} onClick={() => onAcknowledge(e.id)}>
                    <FiCheckCircle size={12} aria-hidden="true" /> Acknowledge
                  </button>
                )}
                {admin && (
                  <button type="button" className="hr-settings__delete" disabled={busy} onClick={() => onDelete(e.id)} aria-label={`Delete ${e.typeLabel} for ${e.employeeName}`}>
                    <FiTrash2 size={12} />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

export default HREvents;
